import React, { useRef, useEffect } from 'react';
import { useCart } from 'frontastic';
import { useMiniCart } from 'context/miniCart';
import CloseIcon from 'icons/close';
import CartItem from 'frontastic/tastics/cart/components/cart-item';
import Link from 'next/link';
import { toast } from 'react-hot-toast';
import { CurrencyHelpers } from 'helpers/currencyHelpers';

const MiniCart = () => {
  const { data: cart, removeItem } = useCart();
  const { hide } = useMiniCart();
  const miniCartRef = useRef<HTMLDivElement>(null);
  const lineItemCount = cart?.lineItems?.reduce((prev, val) => (prev || 0) + (val.count || 0), 0) || 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (miniCartRef.current && !miniCartRef.current.contains(event.target as Node)) {
        hide();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [hide]);

  const handleRemoveItem = async (lineItemId: string) => {
    try {
      await removeItem(lineItemId);
      toast.success('Item has been removed!');
    } catch (error) {
      toast.error('There was an error!');
    }
  };

  return (
    <div
      ref={miniCartRef}
      className="flex h-full w-full flex-col rounded-md bg-primary-lightcream lg:min-w-[590px]"
      data-testid="mini-cart"
    >
      <div className="flex items-center justify-between rounded-md bg-gray-100 p-5 lg:bg-primary-lightcream">
        <h2 className="text-lg font-semibold">Cart ({lineItemCount} items)</h2>
        <button
          onClick={hide}
          className="hidden p-2 lg:block"
          aria-label="Close Mini Cart"
          data-testid="close-mini-cart-button"
        >
          <CloseIcon className="size-4" />
        </button>
      </div>

      <div className="flex grow overflow-y-auto px-5 lg:max-h-[420px]">
        <ul className="flex w-full flex-col">
          {cart?.lineItems?.map((item) => (
            <CartItem
              key={item.lineItemId}
              item={item}
              className="mb-0 w-full border-b border-l-0 border-r-0 border-t-0 border-primary-gold px-0 last:border-b-0"
              isMiniCart
              onRemoveItem={async () => {
                await handleRemoveItem(item.lineItemId as string);
              }}
            />
          ))}
        </ul>
      </div>

      <div className="flex w-full flex-col rounded-md rounded-t-none bg-gray-300 p-5 lg:relative">
        {(cart?.totalPrice?.centAmount ?? 0) > 0 && (
          <div className="mb-4 flex justify-end gap-16 text-sm font-semibold">
            <span>Subtotal:</span>
            <span>{CurrencyHelpers.formatForCurrency(cart?.totalPrice || 0)}</span>
          </div>
        )}
        <div className="mx-auto flex w-full flex-col gap-4 lg:w-auto lg:flex-row">
          <Link
            href="/cart"
            className="text-nowrap rounded bg-primary-gray px-4 py-3 text-center text-sm font-semibold text-white"
            aria-label="View cart and proceed to checkout"
            title="View cart and proceed to checkout"
            onClick={hide}
            data-testid="view-cart-button"
          >
            View Cart & Checkout
          </Link>
          <button
            onClick={hide}
            className="text-nowrap rounded border border-primary-dark px-4 py-3 text-sm font-semibold"
            aria-label="Continue Shopping"
            title="Continue Shopping"
            data-testid="continue-shopping-button"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiniCart;
